import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API_URL } from 'src/assets/http/apis';
import { IApiResponse } from 'src/app/core/interfaces';
import type {
  ICreatePhaseRenewalRequest,
  IPhaseRenewalRequest,
} from 'src/app/features/super-user/interfaces/phase-renewal-request.interface';

/**
 * Transport for the user-facing phase-renewal endpoints.
 *
 * Lives under `core/services/` (rather than inside the lazy SU module)
 * because the renewal banner + submit dialog are mounted on the global
 * authenticated layout — well before the SU bundle is loaded.
 *
 * `submitRenewal` is idempotent on the server: if the caller already has a
 * `New` request, the backend returns the existing record instead of
 * creating a second one.
 */
@Injectable({ providedIn: 'root' })
export class PhaseRenewalService {
  constructor(private http: HttpClient) {}

  /**
   * @description Submits a phase-renewal request for the authenticated caller.
   * @param {ICreatePhaseRenewalRequest} body POST payload (optional free-form message)
   * @returns {Observable<IApiResponse<IPhaseRenewalRequest>>} created (or existing) request
   */
  submitRenewal(body: ICreatePhaseRenewalRequest): Observable<IApiResponse<IPhaseRenewalRequest>> {
    const url = `${environment.apiUrl}${API_URL.PHASE_RENEWALS}`;
    return this.http.post<IApiResponse<IPhaseRenewalRequest>>(url, body);
  }

  /**
   * @description Fetches the caller's most recent `New` renewal request
   * or `null` if none is pending.
   * @returns {Observable<IApiResponse<IPhaseRenewalRequest | null>>} API envelope
   */
  getMine(): Observable<IApiResponse<IPhaseRenewalRequest | null>> {
    const url = `${environment.apiUrl}${API_URL.PHASE_RENEWALS_MINE}`;
    return this.http.get<IApiResponse<IPhaseRenewalRequest | null>>(url);
  }
}
